import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Card, CardBody, Col, Progress, Row, Spinner } from "reactstrap";

import { formatMoneyFromCents } from "../../../../helpers/money";
import {
  getProfessionalGoals,
  ProfessionalGoalsPeriod,
  ProfessionalGoalsSummary,
} from "../../../../services/professionalService";

interface ProfessionalGoalsProgressPanelProps {
  professionalId: number;
}

const PeriodProgressCard = ({
  title,
  subtitle,
  period,
  currency,
  locale,
  t,
  comparisonLines,
}: {
  title: string;
  subtitle?: string;
  period: ProfessionalGoalsPeriod;
  currency: string;
  locale: string;
  t: (key: string) => string;
  comparisonLines?: string[];
}) => {
  const money = (cents: number) => formatMoneyFromCents(cents, locale, currency);

  const commissionLabel =
    period.goals.commission_cents != null
      ? `${money(period.progress.commission_cents)} / ${money(period.goals.commission_cents)}`
      : money(period.progress.commission_cents);

  const appointmentsLabel =
    period.goals.appointments != null
      ? `${period.progress.appointments_completed} / ${period.goals.appointments}`
      : String(period.progress.appointments_completed);

  return (
    <Card className="border shadow-none mb-3">
      <CardBody>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h6 className="mb-0">{title}</h6>
            {subtitle ? <small className="text-muted">{subtitle}</small> : null}
          </div>
        </div>

        <div className="mb-3">
          <div className="d-flex justify-content-between small mb-1">
            <span>{t("studio.professionals.goals.commission")}</span>
            <span className="text-muted">
              {commissionLabel}
              {period.percent.commission != null ? ` · ${period.percent.commission}%` : ""}
            </span>
          </div>
          <Progress
            value={period.percent.commission ?? 0}
            color={period.percent.commission != null && period.percent.commission >= 80 ? "success" : "primary"}
            style={{ height: 8 }}
          />
        </div>

        <div className="mb-2">
          <div className="d-flex justify-content-between small mb-1">
            <span>{t("studio.professionals.goals.appointments")}</span>
            <span className="text-muted">
              {appointmentsLabel}
              {period.percent.appointments != null ? ` · ${period.percent.appointments}%` : ""}
            </span>
          </div>
          <Progress
            value={period.percent.appointments ?? 0}
            color={
              period.percent.appointments != null && period.percent.appointments >= 80
                ? "success"
                : "primary"
            }
            style={{ height: 8 }}
          />
        </div>

        {comparisonLines && comparisonLines.length > 0 ? (
          <ul className="list-unstyled small text-muted mb-0 mt-2">
            {comparisonLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        ) : null}
      </CardBody>
    </Card>
  );
};

const ProfessionalGoalsProgressPanel = ({
  professionalId,
}: ProfessionalGoalsProgressPanelProps) => {
  const { t, i18n } = useTranslation();
  const [goals, setGoals] = useState<ProfessionalGoalsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadGoals = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await getProfessionalGoals(professionalId);
      setGoals(response.data);
    } catch (error: any) {
      setGoals(null);
      toast.error(error?.data || t("studio.professionals.goals.toast.loadError"));
    } finally {
      setIsLoading(false);
    }
  }, [professionalId, t]);

  useEffect(() => {
    void loadGoals();
  }, [loadGoals]);

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <Spinner size="sm" className="me-2" />
        {t("studio.professionals.goals.loading")}
      </div>
    );
  }

  if (!goals) {
    return (
      <p className="text-muted mb-4">{t("studio.professionals.goals.empty")}</p>
    );
  }

  const comparisonLines: string[] = [];
  const cmp = goals.week_comparison;
  if (cmp?.commission_percent_delta != null) {
    comparisonLines.push(
      cmp.commission_percent_delta === 0
        ? t("studio.professionals.goals.vsPreviousEqual")
        : t("studio.professionals.goals.vsPreviousCommission", {
            percent: `${cmp.commission_percent_delta > 0 ? "+" : ""}${cmp.commission_percent_delta}`,
          })
    );
  }
  if (cmp?.appointments_percent_delta != null) {
    const line =
      cmp.appointments_percent_delta === 0
        ? t("studio.professionals.goals.vsPreviousEqual")
        : t("studio.professionals.goals.vsPreviousAppointments", {
            percent: `${cmp.appointments_percent_delta > 0 ? "+" : ""}${cmp.appointments_percent_delta}`,
          });
    if (!comparisonLines.includes(line)) {
      comparisonLines.push(line);
    }
  }

  return (
    <div className="mb-4">
      <h5 className="mb-1">{t("studio.professionals.goals.title")}</h5>
      <p className="text-muted small mb-3">{t("studio.professionals.goals.subtitle")}</p>

      {goals.near_goal.any ? (
        <div className="alert alert-warning py-2" role="status">
          {goals.near_goal.hint || t("studio.professionals.goals.near")}
        </div>
      ) : null}

      <Row className="g-3">
        <Col md={4}>
          <PeriodProgressCard
            title={t("studio.professionals.goals.thisWeek")}
            subtitle={`${goals.week.start} → ${goals.week.end}`}
            period={goals.week}
            currency={goals.currency}
            locale={i18n.language}
            t={t}
            comparisonLines={comparisonLines}
          />
        </Col>
        <Col md={4}>
          <PeriodProgressCard
            title={t("studio.professionals.goals.previousWeek")}
            subtitle={`${goals.previous_week.start} → ${goals.previous_week.end}`}
            period={goals.previous_week}
            currency={goals.currency}
            locale={i18n.language}
            t={t}
          />
        </Col>
        <Col md={4}>
          <PeriodProgressCard
            title={t("studio.professionals.goals.thisMonth")}
            subtitle={goals.month.key}
            period={goals.month}
            currency={goals.currency}
            locale={i18n.language}
            t={t}
          />
        </Col>
      </Row>
    </div>
  );
};

export default ProfessionalGoalsProgressPanel;
